<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="head-icons ms-2">
          <a id="collapse-header" @click="toggleHeader"><i class="ti ti-chevrons-up"></i></a>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-xl-5">
          <div class="card">
            <div class="card-header border-0 pb-0">
              <h5 class="mb-0">Payroll Adjustments & Reliefs</h5>
              <p class="text-muted mb-0 mt-1">
                Capture employee HELB, pension, insurance, mortgage, NSSF voluntary, and manual loan adjustments used during payroll calculation.
              </p>
            </div>

            <div class="card-body">
              <div class="mb-3">
                <label class="form-label fw-semibold">Payroll Month</label>
                <Datepicker
                  v-model="period"
                  :format="'YYYY-MM'"
                  class="form-control"
                  :clearable="false"
                />
                <div class="text-muted small mt-1">This will apply to the selected month.</div>
              </div>

              <div class="mb-3">
                <EmployeeSelectDropdown
                  v-model="employeeId"
                  :employees="employeeDropdownEmployees"
                  mode="single"
                  label="Employee"
                  help="Select employee whose deductions you are managing."
                  :disabled="saving"
                />
              </div>

              <div v-if="!employeeId" class="alert alert-info">
                <i class="ti ti-info-circle me-1"></i>
                Select a payroll month and employee to begin.
              </div>

              <div v-else>
                <div class="dq-surface p-3 border rounded bg-light mb-3">
                  <div class="fw-semibold mb-1">What these affect</div>
                  <ul class="mb-0 small text-muted">
                    <li><b>Pension</b> + <b>Mortgage interest</b> reduce taxable income and lower PAYE</li>
                    <li><b>Insurance premium</b> gives insurance relief and lowers PAYE</li>
                    <li><b>NSSF voluntary</b> reduces net pay and feeds the NSSF monthly submission file</li>
                    <li><b>SACCO/HELB</b> reduce net pay as cash deductions</li>
                  </ul>
                </div>

                <div class="row g-2">
                  <div class="col-md-6">
                    <label class="form-label">Manual Loan Repayment</label>
                    <div class="input-group">
                      <span class="input-group-text">{{ currency }}</span>
                      <input v-model.number="form.sacco_loan" type="number" min="0" class="form-control" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">HELB</label>
                    <div class="input-group">
                      <span class="input-group-text">{{ currency }}</span>
                      <input v-model.number="form.helb" type="number" min="0" class="form-control" />
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Mortgage Interest</label>
                    <div class="input-group">
                      <span class="input-group-text">{{ currency }}</span>
                      <input v-model.number="form.mortgage_interest" type="number" min="0" class="form-control" />
                    </div>
                    <div class="text-muted small mt-1">Used to reduce taxable income (PAYE).</div>
                  </div>

                  <div class="col-md-6">
                    <label class="form-label">Registered Pension</label>
                    <div class="input-group">
                      <span class="input-group-text">{{ currency }}</span>
                      <input v-model.number="form.pension_contrib" type="number" min="0" class="form-control" />
                    </div>
                    <div class="text-muted small mt-1">Used to reduce taxable income (PAYE).</div>
                  </div>

                  <div class="col-12">
                    <label class="form-label">Insurance Premium</label>
                    <div class="input-group">
                      <span class="input-group-text">{{ currency }}</span>
                      <input v-model.number="form.insurance_premium" type="number" min="0" class="form-control" />
                    </div>
                    <div class="text-muted small mt-1">Used for insurance relief in PAYE.</div>
                  </div>

                  <div class="col-12">
                    <label class="form-label">NSSF Voluntary</label>
                    <div class="input-group">
                      <span class="input-group-text">{{ currency }}</span>
                      <input v-model.number="form.nssf_voluntary" type="number" min="0" class="form-control" />
                    </div>
                    <div class="text-muted small mt-1">Extra employee NSSF top-up. Reduces net pay and appears in the monthly NSSF export.</div>
                  </div>
                </div>

                <div v-if="error" class="alert alert-danger mt-3">
                  <i class="ti ti-alert-circle me-1"></i> {{ error }}
                </div>

                <div class="d-flex gap-2 mt-3">
                  <button class="btn btn-primary w-100" :disabled="saving" @click="save">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                    <i class="ti ti-device-floppy me-1"></i> Save
                  </button>

                  <button class="btn btn-outline-dark" :disabled="saving" @click="loadExisting">
                    <i class="ti ti-refresh me-1"></i> Reload
                  </button>
                </div>

                <div class="text-muted small mt-2">
                  This updates <code>employee_salaries</code> so payroll uses it automatically.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-7">
          <div class="card">
            <div class="card-header border-0 pb-0">
              <h5 class="mb-0">Preview for Selected Month</h5>
              <p class="text-muted mb-0 mt-1">
                Shows the stored values used during payroll generation.
              </p>
            </div>

            <div class="card-body">
              <div v-if="!employeeId" class="text-muted">Select employee to preview.</div>

              <div v-else class="row g-2">
                <div class="col-md-4">
                  <div class="border rounded p-3">
                    <div class="text-muted small">Manual Loan Repayment</div>
                    <div class="fw-bold">{{ money(form.sacco_loan) }}</div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="border rounded p-3">
                    <div class="text-muted small">HELB</div>
                    <div class="fw-bold">{{ money(form.helb) }}</div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="border rounded p-3">
                    <div class="text-muted small">Mortgage</div>
                    <div class="fw-bold">{{ money(form.mortgage_interest) }}</div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="border rounded p-3">
                    <div class="text-muted small">Pension</div>
                    <div class="fw-bold">{{ money(form.pension_contrib) }}</div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="border rounded p-3">
                    <div class="text-muted small">Insurance Premium</div>
                    <div class="fw-bold">{{ money(form.insurance_premium) }}</div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="border rounded p-3">
                    <div class="text-muted small">NSSF Voluntary</div>
                    <div class="fw-bold">{{ money(form.nssf_voluntary) }}</div>
                  </div>
                </div>

                <div class="col-12">
                  <div class="alert alert-warning mt-2 mb-0">
                    <i class="ti ti-alert-triangle me-1"></i>
                    This preview is deductions-only. PAYE/net pay final values are computed during payroll generation.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="text-muted small mt-2">
            Tip: Use payroll, loans, and savings reports together to validate these adjustments against generated payroll.
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p class="mb-0">
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import Datepicker from "vue3-datepicker";
import moment from "moment";
import { storeToRefs } from "pinia";
import { toast } from "@/utils/toast";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useCompanyStore } from "@/stores/companyStore";
import EmployeeSelectDropdown from "@/components/common/EmployeeSelectDropdown.vue";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";

const title = "Payroll Adjustments";
const text = "Payroll Company Settings";
const text1 = "Payroll Adjustments";

const employeeStore = useEmployeeStore();
const salaryStore = useEmployeeSalaryStore();
const companyStore = useCompanyStore();

const { employees } = storeToRefs(employeeStore);
const { companies, activeCompanyId } = storeToRefs(companyStore);

const activeCompany = computed(() =>
  companies.value.find((c) => c.id === activeCompanyId.value),
);

const currency = computed(() => activeCompany.value?.currency_code || "KES");

function money(v) {
  return (
    currency.value +
    " " +
    Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })
  );
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

const period = ref(new Date());
const employeeId = ref(null);

const saving = ref(false);
const error = ref("");

const form = ref({
  sacco_loan: 0,
  helb: 0,
  mortgage_interest: 0,
  pension_contrib: 0,
  insurance_premium: 0,
  nssf_voluntary: 0,
});

const activeEmployees = computed(() =>
  employees.value.filter(
    (e) => String(e?.status || "").trim().toLowerCase() === "active",
  ),
);

const employeeDropdownEmployees = computed(() =>
  activeEmployees.value.map((e) => ({
    id: Number(e.id),
    name: `${e.first_name} ${e.last_name || ""}`.trim(),
    employee_id: e.employee_id,
    avatar_url: e.avatar_url || "default.jpg",
    department: e.department?.name || "",
    designation: e.designation?.name || "",
  })),
);

const yyyymm = computed(() => moment(period.value).format("YYYY-MM"));

async function loadExisting() {
  error.value = "";
  if (!employeeId.value) return;

  try {
    const data = await salaryStore.fetchPayrollAdjustments(
      employeeId.value,
      yyyymm.value,
    );

    if (!data?.has_record) {
      form.value = {
        sacco_loan: 0,
        helb: 0,
        mortgage_interest: 0,
        pension_contrib: 0,
        insurance_premium: 0,
        nssf_voluntary: 0,
      };
      toast("No saved adjustments for this month yet. Enter values then Save.", "info");
      return;
    }

    form.value = {
      sacco_loan: Number(data.adjustments?.sacco_loan) || 0,
      helb: Number(data.adjustments?.helb) || 0,
      mortgage_interest: Number(data.adjustments?.mortgage_interest) || 0,
      pension_contrib: Number(data.adjustments?.pension_contrib) || 0,
      insurance_premium: Number(data.adjustments?.insurance_premium) || 0,
      nssf_voluntary: Number(data.adjustments?.nssf_voluntary) || 0,
    };

    toast("Loaded saved values for this month.", "success");
  } catch (e) {
    error.value = e.response?.data?.message || e.message;
  }
}

async function save() {
  error.value = "";
  if (!employeeId.value) {
    toast("Select employee first.", "error");
    return;
  }

  saving.value = true;
  try {
    await salaryStore.savePayrollAdjustments({
      employee_id: employeeId.value,
      period: yyyymm.value,
      sacco_loan: form.value.sacco_loan,
      helb: form.value.helb,
      mortgage_interest: form.value.mortgage_interest,
      pension_contrib: form.value.pension_contrib,
      insurance_premium: form.value.insurance_premium,
      nssf_voluntary: form.value.nssf_voluntary,
    });

    toast("Saved successfully. Payroll will use these values automatically.", "success");
    await loadExisting();
  } catch (e) {
    error.value =
      e.response?.data?.message ||
      e.message ||
      "Failed to save payroll adjustments.";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  if (!employees.value.length) {
    await employeeStore.fetchEmployees({ status: "Active", per_page: 5000 });
  }
});

watch([employeeId, yyyymm], () => {
  if (employeeId.value) loadExisting();
});
</script>


